import React, {useMemo, useRef} from 'react';
import {LineChart} from '@mui/x-charts/LineChart';
import {Typography, Box} from '@mui/material';
import {useZoomAwareSize} from '../../../hooks/utils/useElmResize';
import './ArrLineChart.scss';

export interface ArrLineChartData {
  xAxisData: string;
  yAxisData: number;
}

export interface ArrLineChartProps {
  data: ArrLineChartData[];
  title?: string;
  className?: string;
  preserveZoom?: boolean;
  xAxisConfig?: {[key: string]: any};
  yAxisConfig?: {[key: string]: any};
}

const ArrLineChart: React.FC<ArrLineChartProps> = ({
  data,
  title = '',
  className,
  preserveZoom = false,
  xAxisConfig = {},
  yAxisConfig = {},
}) => {
  const isAllZero = data.every((d) => d.yAxisData === 0);
  const [lineChartContainerRef, size] = useZoomAwareSize();
  const titleRef = useRef<HTMLElement | null>(null);

  const chartHeight = useMemo(() => {
    if (lineChartContainerRef?.current && preserveZoom) {
      const titleHeight = titleRef.current?.clientHeight || 0;
      return size.height - titleHeight;
    }
    return undefined;
  }, [preserveZoom, size]);

  return (
    <Box
      data-testid="line-chart-container"
      className={`arr-line-chart-container ${isAllZero ? 'allZero' : ''} ${className ?? ''}`}
      ref={lineChartContainerRef}
    >
      {title && (
        <Typography
          ref={titleRef}
          className="title-text"
          data-testid={`review-rate-title`}
        >
          {title}
        </Typography>
      )}
      <div
        className="chart-container-wrapper"
        data-testid="chart-container-wrapper"
      >
        <Box className="chart-container">
          <LineChart
            data-testid="rate-trend"
            height={chartHeight}
            xAxis={[
              {
                data: data.map((d) => d.xAxisData),
                scaleType: 'point',
                tickLabelStyle: {
                  fontSize: 12,
                  lineHeight: '1.125rem',
                },
                disableLine: true,
                disableTicks: true,
                offset: 15,
                ...xAxisConfig,
              },
            ]}
            yAxis={[
              {
                min: 0,
                tickLabelStyle: {
                  fontSize: 14,
                  lineHeight: '1.25rem',
                },
                disableLine: true,
                disableTicks: true,
                offset: 8,
                ...yAxisConfig,
              },
            ]}
            series={[
              {
                data: data.map((d) => d.yAxisData),
                curve: 'monotoneX',
                color: '#6E87FF',
                showMark: true,
                valueFormatter: (v) => `Value: ${v}`,
                ...(isAllZero && {area: false, showLine: false}),
              },
            ]}
            grid={{vertical: false, horizontal: true}}
            margin={{top: 20, right: 20, bottom: 20, left: 0}}
            axisHighlight={{x: 'none'}}
            slotProps={{
              tooltip: {
                className: 'arr-line-chart-container__tooltip',
                placement: 'top',
              },
              axisTickLabel: {
                className: 'arr-line-chart-container__axisLabel',
              },
            }}
          />
        </Box>
      </div>
    </Box>
  );
};

export default ArrLineChart;
